import { gql } from "@apollo/client";

/**
 * Whether the backend serves `Client.prov`, asked once per Apollo client.
 *
 * Why a probe and not `prov @include(if: $withProv)`: a GraphQL document that
 * names a field the schema does not have fails VALIDATION as a whole, and
 * validation runs before any directive is evaluated. `@include(if: false)` on
 * an unknown field is still "Cannot query field prov", so the whole inbox query
 * fails and the inbox renders empty. The dashboard ships on merge and the
 * backend waits for a deploy window — and can be rolled back — so the only
 * safe document is one that names `prov` exclusively when the schema has it.
 *
 * Every failure answers `false` (no seal, inbox intact), and a mid-session
 * rollback is caught by `queryWithProvenance`, which retries once without the
 * field. Asking for the seal must never cost the inbox.
 */

export const PROVENANCE_PROBE_QUERY = gql`
  query ConversationProvenanceProbe {
    __type(name: "ConversationProvenance") {
      name
    }
  }
`;

const PROVENANCE_TYPE_NAME = "ConversationProvenance";

/**
 * How long the first inbox load may wait for the answer. It is one round trip
 * once per session; past this the answer is `false`, so a hung probe costs at
 * most this delay once and never the inbox itself.
 */
const DEFAULT_TIMEOUT_MS = 2000;

/** One answer (a promise of a boolean) per Apollo client instance. */
let answers = new WeakMap();

/** Test seam: module state would otherwise leak between test cases. */
export function resetProvenanceProbe() {
  answers = new WeakMap();
}

function usableClient(apolloClient) {
  return (
    !!apolloClient &&
    typeof apolloClient === "object" &&
    typeof apolloClient.query === "function"
  );
}

function withTimeout(promise, timeoutMs) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(false);
      }
    );
  });
}

async function runProbe(apolloClient) {
  try {
    const result = await apolloClient.query({
      query: PROVENANCE_PROBE_QUERY,
      fetchPolicy: "no-cache",
    });
    const type = result && result.data ? result.data.__type : null;
    return !!type && typeof type === "object" && type.name === PROVENANCE_TYPE_NAME;
  } catch (e) {
    return false;
  }
}

/**
 * Resolves `true` only when the schema has `ConversationProvenance`. Never
 * rejects. Concurrent callers share the one in-flight probe.
 */
export function supportsProvenance(apolloClient, options) {
  if (!usableClient(apolloClient)) return Promise.resolve(false);
  const cached = answers.get(apolloClient);
  if (cached) return cached;

  const timeoutMs =
    options && typeof options.timeoutMs === "number" && options.timeoutMs > 0
      ? options.timeoutMs
      : DEFAULT_TIMEOUT_MS;
  const answer = withTimeout(runProbe(apolloClient), timeoutMs);
  answers.set(apolloClient, answer);
  return answer;
}

/** Remembers that this client's backend does not serve `prov` after all. */
export function markProvenanceUnsupported(apolloClient) {
  if (!usableClient(apolloClient)) return;
  answers.set(apolloClient, Promise.resolve(false));
}

const PROVENANCE_ERROR_PATTERNS = [
  /Cannot query field "prov"/,
  /Unknown type "ConversationProvenance"/,
];

function mentionsProvenance(message) {
  if (typeof message !== "string") return false;
  return PROVENANCE_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/** Whether an Apollo error is the schema refusing the `prov` selection. */
export function isProvenanceFieldError(error) {
  try {
    if (!error || typeof error !== "object") return false;
    if (mentionsProvenance(error.message)) return true;
    const list = Array.isArray(error.graphQLErrors) ? error.graphQLErrors : [];
    return list.some((item) => !!item && mentionsProvenance(item.message));
  } catch (e) {
    return false;
  }
}

/**
 * Runs `apolloClient.query(buildOptions(withProv))`, with `withProv` from the
 * probe. If the backend rejects the `prov` selection (rolled back after the
 * probe said yes), it remembers that and retries ONCE without it, so the
 * caller gets its conversations either way. Any other error propagates as is.
 */
export async function queryWithProvenance(apolloClient, buildOptions) {
  const withProv = await supportsProvenance(apolloClient);
  try {
    return await apolloClient.query(buildOptions(withProv));
  } catch (error) {
    if (!withProv || !isProvenanceFieldError(error)) throw error;
    markProvenanceUnsupported(apolloClient);
    return apolloClient.query(buildOptions(false));
  }
}
